// Sprint 29.0 — Controles de caja/turno para el tablero Valet en sucursales "SOLO_VALET".
// Encapsula: Abrir Turno, Registrar Gasto, Reimprimir Corte y Cerrar Turno (con cuadre de caja
// y comprobante térmico de corte). Reutiliza los endpoints operativos existentes; NO toca BD ni precios.
import { useCallback, useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import {
  Banknote, Printer, Power, DoorOpen, Loader2, CreditCard, Moon, Ticket as TicketIcon,
} from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CurrencyField } from "@/components/ui/forms";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { loadPrinterConfig, buildCorteTicket, buildGastoTicket, printOrFallback } from "@/lib/escposHelper";
import { CorteThermalReceipt } from "@/components/terminal/CorteThermalReceipt";
import { GastoThermalReceipt } from "@/components/terminal/GastoThermalReceipt";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/datetime";

const CajaTurnoBar = forwardRef(function CajaTurnoBar({ sucursal, caja, onCajaChange, brand, onAbrirTurno, valetSinCosto = false, showButtons = true, onState }, ref) {
  const sid = sucursal?.id;
  const { user } = useAuth();

  // Comprobante de gasto (impresión + reimpresión; datos de solo lectura)
  const [gastoReceipt, setGastoReceipt] = useState(null);
  const [gastosTurno, setGastosTurno] = useState([]);
  const [gastosLoading, setGastosLoading] = useState(false);

  const [perms, setPerms] = useState({ puede_realizar_corte: false, puede_registrar_gastos: false, requiere_permiso_corte: false, requiere_permiso_gastos: false });

  // Gasto
  const [gastoOpen, setGastoOpen] = useState(false);
  const [gastoMonto, setGastoMonto] = useState("");
  const [gastoCategoria, setGastoCategoria] = useState("Insumos");
  const [gastoConcepto, setGastoConcepto] = useState("");
  const [gastoSaving, setGastoSaving] = useState(false);

  // Cierre
  const [closeOpen, setCloseOpen] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [efectivo, setEfectivo] = useState("");

  // Impresión de corte
  const [corteReceipt, setCorteReceipt] = useState(null);
  const [paperWidth, setPaperWidth] = useState(() => Number(sessionStorage.getItem("pap_paper_width")) || 80);

  useEffect(() => {
    if (!sid) return;
    api.get(`/operativa/mis-permisos?id_sucursal=${sid}`).then((r) => setPerms(r.data)).catch(() => {});
  }, [sid]);

  const abrirTurno = () => onAbrirTurno?.();

  const registrarGasto = async () => {
    const monto = parseFloat(gastoMonto);
    if (isNaN(monto) || monto <= 0) return toast.error("Ingresa un monto válido");
    if (!gastoConcepto.trim()) return toast.error("El concepto es obligatorio");
    setGastoSaving(true);
    try {
      const { data: g } = await api.post("/operativa/gastos", { id_caja: caja.id_caja, monto, categoria: gastoCategoria, concepto: gastoConcepto.trim() });
      toast.success("Gasto registrado");
      setGastoOpen(false); setGastoMonto(""); setGastoConcepto(""); setGastoCategoria("Insumos");
      imprimirGasto(g); // ofrecer impresión del comprobante (best-effort; en nativo abre el diálogo)
      cargarGastosTurno();
      if (closeOpen) { try { const { data } = await api.get(`/operativa/turno-resumen?id_caja=${caja.id_caja}`); setResumen(data); } catch { /* noop */ } }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGastoSaving(false); }
  };

  // Sprint 42.0 — Comprobante térmico de GASTO / NÓMINA (datos de solo lectura; nunca re-registra ni descuenta).
  const imprimirGasto = (gasto, { reimpresion = false } = {}) => {
    if (!gasto) return;
    const cfg = loadPrinterConfig();
    const brandName = brand || sucursal?.nombre || "PARKNOVA";
    const payload = {
      brand: brandName, sucursal: sucursal?.nombre,
      operador: gasto.operador || user?.usuario_corto || user?.email || "",
      fecha: gasto.fecha ? fmtDateTime(gasto.fecha) : fmtDateTime(new Date()),
      categoria: gasto.categoria, concepto: gasto.concepto, monto: gasto.monto, reimpresion,
    };
    const doNative = () => { setGastoReceipt(payload); printThermalCorte(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildGastoTicket({ ...payload, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Comprobante enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  const cargarGastosTurno = async () => {
    if (!caja?.id_caja) return;
    setGastosLoading(true);
    try { const { data } = await api.get(`/operativa/gastos?id_caja=${caja.id_caja}`); setGastosTurno(data || []); }
    catch { /* noop */ }
    finally { setGastosLoading(false); }
  };
  useEffect(() => { if (gastoOpen) cargarGastosTurno(); }, [gastoOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const printThermalCorte = useCallback((width) => {
    const w = Number(width) || paperWidth || 80;
    setPaperWidth(w);
    sessionStorage.setItem("pap_paper_width", String(w));
    let tag = document.getElementById("thermal-page-size");
    if (!tag) { tag = document.createElement("style"); tag.id = "thermal-page-size"; document.head.appendChild(tag); }
    tag.textContent = `@page { size: ${w}mm auto; margin: 0; } .thermal-receipt { width: ${w}mm !important; }`;
    setTimeout(() => {
      // iOS Safari captura la impresión desde la posición de scroll; el corte (position:absolute; top:0)
      // queda fuera de vista si la página está scrolleada (p.ej. al cerrar turno en móvil) → hoja en blanco.
      // Volver al tope garantiza que el corte entre en el área impresa. Mismo fix aplicado en Terminal.printThermal.
      try { window.scrollTo(0, 0); } catch { /* noop */ }
      window.print();
    }, 120);
  }, [paperWidth]);

  const imprimirCorte = (snapshot) => {
    const r = snapshot; if (!r) return;
    const cfg = loadPrinterConfig();
    const brandName = brand || sucursal?.nombre || "PARKNOVA";
    const reimpresion = !!snapshot.reimpresion;
    const doNative = () => { setCorteReceipt({ ...r, brand: brandName, sucursalNombre: sucursal?.nombre, reimpresion }); printThermalCorte(cfg.papel ? Number(cfg.papel) : paperWidth); };
    if (cfg.modo === "nativo") { doNative(); return; }
    try {
      const bytes = buildCorteTicket({ brand: brandName, sucursal: sucursal?.nombre, operador: r.operador, resumen: r, efectivoContado: r.efectivoContado, reimpresion, cfg: { ...cfg, abrirCajon: false } });
      printOrFallback(bytes, cfg).then((res) => { if (res === "nativo") doNative(); else toast.success("Corte enviado a la impresora"); }).catch(() => doNative());
    } catch { doNative(); }
  };

  const reimprimirUltimoCorte = async () => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/operativa/ultimo-corte?id_sucursal=${sid}`);
      imprimirCorte({ ...data, efectivoContado: data.efectivo_cierre, reimpresion: true });
      toast.success("Reimprimiendo último corte");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No hay cortes para reimprimir");
    }
  };

  const openCierre = async () => {
    setCloseOpen(true); setResumen(null); setEfectivo(""); setLoadingResumen(true);
    try {
      const { data } = await api.get(`/operativa/turno-resumen?id_caja=${caja.id_caja}`);
      setResumen(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoadingResumen(false); }
  };

  const cerrandoRef = useRef(false);
  const [cerrando, setCerrando] = useState(false);
  const cerrarTurno = async () => {
    if (cerrandoRef.current) return; // guard síncrono anti doble-clic (inmune al mismo tick)
    const val = parseFloat(efectivo);
    if (isNaN(val)) return toast.error("Ingresa el efectivo de cierre");
    cerrandoRef.current = true;
    setCerrando(true);
    try {
      await api.post("/operativa/caja/cerrar", { id_caja: caja.id_caja, efectivo_cierre: val });
      imprimirCorte({ ...resumen, efectivoContado: val });
      toast.success("Turno cerrado");
      setCloseOpen(false); setEfectivo(""); setResumen(null);
      onCajaChange?.(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { cerrandoRef.current = false; setCerrando(false); }
  };

  // Valet SIN COSTO ($0) — cierre de evento: sin cuadre de caja; registra auditoría de Factor de Riesgo.
  const [noCostOpen, setNoCostOpen] = useState(false);
  const [noCostResumen, setNoCostResumen] = useState(null);
  const [noCostLoading, setNoCostLoading] = useState(false);
  const [noCostSaving, setNoCostSaving] = useState(false);

  const openCierreEvento = async () => {
    setNoCostOpen(true); setNoCostResumen(null); setNoCostLoading(true);
    try {
      const { data } = await api.get(`/operativa/turno-resumen?id_caja=${caja.id_caja}`);
      setNoCostResumen(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setNoCostLoading(false); }
  };

  const cerrarEventoSinCosto = async () => {
    setNoCostSaving(true);
    try {
      await api.post("/operativa/valet/cerrar-sin-costo", { id_caja: caja.id_caja });
      toast.success("Evento cerrado · auditoría de Factor de Riesgo registrada");
      setNoCostOpen(false); setNoCostResumen(null);
      onCajaChange?.(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setNoCostSaving(false); }
  };

  const canGasto = (!perms.requiere_permiso_gastos || perms.puede_registrar_gastos);
  const canReimprimir = !valetSinCosto;

  // Expone las acciones para que el menú móvil de Valet las dispare (sin duplicar diálogos).
  // Sin arreglo de dependencias: el handle se recalcula por render para reflejar siempre
  // los últimos closures (abrir/gasto/reimprimir/cerrar), evitando handlers obsoletos.
  useImperativeHandle(ref, () => ({
    hasCaja: !!caja,
    canGasto, canReimprimir, valetSinCosto,
    abrirTurno,
    openGasto: () => setGastoOpen(true),
    reimprimir: reimprimirUltimoCorte,
    cerrar: () => (valetSinCosto ? openCierreEvento() : openCierre()),
  }));

  useEffect(() => {
    onState?.({ hasCaja: !!caja, canGasto, canReimprimir, valetSinCosto });
  }, [caja, canGasto, canReimprimir, valetSinCosto, onState]);

  const btn = "btn-hard h-10 px-3 bg-white/5 border border-white/20 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-white/10";

  return (
    <>
      {/* --- Botones de encabezado --- */}
      {showButtons && (!caja ? (
        <button onClick={abrirTurno} data-testid="valet-abrir-turno-btn"
          className="btn-hard h-10 px-3 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] text-xs font-black uppercase tracking-wider flex items-center gap-2">
          <DoorOpen className="h-4 w-4" /> Abrir turno
        </button>
      ) : (
        <>
          {(!perms.requiere_permiso_gastos || perms.puede_registrar_gastos) && (
            <button onClick={() => setGastoOpen(true)} data-testid="valet-registrar-gasto"
              className="btn-hard h-10 px-3 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-[#F59E0B]/25">
              <Banknote className="h-4 w-4" /> <span className="hidden md:inline">Registrar Gasto</span>
            </button>
          )}
          {!valetSinCosto && (
            <button onClick={reimprimirUltimoCorte} data-testid="valet-reimprimir-corte" title="Reimprimir corte" className={btn}>
              <Printer className="h-4 w-4" /> <span className="hidden lg:inline">Reimprimir corte</span>
            </button>
          )}
          <button onClick={valetSinCosto ? openCierreEvento : openCierre} data-testid="valet-cerrar-turno" title={valetSinCosto ? "Cerrar evento" : "Cerrar turno"} className={btn}>
            <Power className="h-4 w-4" /> <span className="hidden md:inline">{valetSinCosto ? "Cerrar evento" : "Cerrar turno"}</span>
          </button>
        </>
      ))}

      {/* --- Registrar gasto --- */}
      <Dialog open={gastoOpen} onOpenChange={setGastoOpen}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-white sm:max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="valet-gasto-modal">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2"><Banknote className="h-5 w-5 text-[#F59E0B]" /> Registrar Gasto / Nómina</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Monto ($ MXN)</label>
              <CurrencyField inputMode="decimal" value={gastoMonto} onChange={(v) => setGastoMonto(v)} placeholder="0.00"
                data-testid="valet-gasto-monto-input" className="mt-1 h-12 rounded-none bg-white/5 border-white/20 text-white text-lg font-display" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Categoría</label>
              <Select value={gastoCategoria} onValueChange={setGastoCategoria}>
                <SelectTrigger className="mt-1 h-11 rounded-none bg-white/5 border-white/20 text-white" data-testid="valet-gasto-categoria-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nómina/Salario">Nómina/Salario</SelectItem>
                  <SelectItem value="Insumos">Insumos</SelectItem>
                  <SelectItem value="Mantenimiento">Mantenimiento</SelectItem>
                  <SelectItem value="Pago a Proveedor">Pago a Proveedor</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Concepto (obligatorio)</label>
              <Input value={gastoConcepto} onChange={(e) => setGastoConcepto(e.target.value)} placeholder="Ej. Pago de nómina turno noche"
                data-testid="valet-gasto-concepto-input" className="mt-1 h-11 rounded-none bg-white/5 border-white/20 text-white" />
            </div>
            <button onClick={registrarGasto} disabled={gastoSaving} data-testid="valet-gasto-guardar-btn"
              className="btn-hard w-full h-12 bg-[#F59E0B] text-[#0A0A0A] border border-[#0A0A0A] font-black uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
              {gastoSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />} Guardar gasto
            </button>

            {/* Comprobantes de gasto del turno — reimpresión (no re-registra ni descuenta) */}
            <div className="pt-2 border-t border-white/10" data-testid="valet-gasto-comprobantes">
              <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2">Comprobantes del turno</div>
              {gastosLoading ? (
                <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-white/40" /></div>
              ) : gastosTurno.length === 0 ? (
                <div className="text-xs text-white/40 py-2">Aún no hay gastos registrados en este turno.</div>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {gastosTurno.map((g) => (
                    <div key={g.id} data-testid={`valet-gasto-row-${g.id}`} className="flex items-center gap-2 bg-white/5 border border-white/10 p-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-sm text-white">${Number(g.monto || 0).toFixed(2)} <span className="text-[10px] uppercase tracking-wider text-[#F59E0B]">{g.categoria || "Otro"}</span></div>
                        <div className="text-xs text-white/60 truncate">{g.concepto || "-"}</div>
                        <div className="text-[10px] text-white/40">{fmtDateTime(g.fecha)}{g.operador ? ` · ${g.operador}` : ""}</div>
                      </div>
                      <button onClick={() => imprimirGasto(g, { reimpresion: true })} data-testid={`valet-gasto-reimprimir-${g.id}`}
                        className="btn-hard h-9 px-3 bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#F59E0B]/25 shrink-0">
                        <Printer className="h-3.5 w-3.5" /> Reimprimir
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Cerrar turno · cuadre de caja --- */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="rounded-none border-white/20 bg-[#0A0A0A] text-white sm:max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="valet-cierre-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-white">Cerrar turno · Cuadre de caja</DialogTitle>
            <DialogDescription className="text-white/50">Revisa el desglose de ingresos y captura el efectivo contado.</DialogDescription>
          </DialogHeader>
          {loadingResumen ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FFCC00]" /></div>
          ) : (
            <div className="space-y-4 pt-1" data-testid="valet-cierre-resumen">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-white/15 bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-white/60 mb-2"><Banknote className="h-4 w-4 text-[#34D399]" /><span className="text-[10px] uppercase tracking-wider font-bold">Efectivo</span></div>
                  <p className="font-display text-2xl font-black text-[#34D399]" data-testid="valet-resumen-efectivo">${Number(resumen?.ingresos_efectivo || 0).toFixed(2)}</p>
                </div>
                <div className="border border-white/15 bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-white/60 mb-2"><CreditCard className="h-4 w-4 text-[#009EE3]" /><span className="text-[10px] uppercase tracking-wider font-bold">Tarjeta · MP</span></div>
                  <p className="font-display text-2xl font-black text-[#38BDF8]" data-testid="valet-resumen-tarjeta">${Number(resumen?.ingresos_tarjeta || 0).toFixed(2)}</p>
                </div>
              </div>
              <div className="border border-white/10 divide-y divide-white/10 text-sm">
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Fondo inicial</span><span className="font-mono">${Number(resumen?.fondo_inicial || 0).toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Estacionamiento</span><span className="font-mono">${Number(resumen?.ventas_estacionamiento || 0).toFixed(2)}</span></div>
                {Number(resumen?.ventas_valet || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-white/60">Servicio Valet</span><span className="font-mono">${Number(resumen?.ventas_valet || 0).toFixed(2)}</span></div>
                )}
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Productos (POS)</span><span className="font-mono" data-testid="resumen-productos">${Number(resumen?.ventas_productos || 0).toFixed(2)}</span></div>
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Servicios adicionales</span><span className="font-mono" data-testid="resumen-servicios">${Number(resumen?.ventas_servicios || 0).toFixed(2)}</span></div>
                {Number(resumen?.conteo_pernoctas_cobradas || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-[#818CF8] flex items-center gap-1"><Moon className="h-3.5 w-3.5" />(+) Pernoctas ({resumen?.conteo_pernoctas_cobradas})</span><span className="font-mono text-[#818CF8]">${Number(resumen?.monto_total_pernoctas_cobradas || 0).toFixed(2)}</span></div>
                )}
                {Number(resumen?.ventas_propinas || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-[#10B981]">Propinas (tarjeta · no facturable)</span><span className="font-mono text-[#10B981]">${Number(resumen?.ventas_propinas || 0).toFixed(2)}</span></div>
                )}
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Tickets cobrados</span><span className="font-mono">{resumen?.tickets_pagados ?? 0}</span></div>
                {Number(resumen?.conteo_cortesias_valet || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-[#A78BFA] flex items-center gap-1"><TicketIcon className="h-3.5 w-3.5" />Cortesías de Valet otorgadas</span><span className="font-mono text-[#A78BFA]">{resumen?.conteo_cortesias_valet}</span></div>
                )}
                <div className="flex justify-between px-4 py-2.5 bg-white/5"><span className="text-xs uppercase tracking-wider font-bold text-white/70">Efectivo recaudado en turno / caja</span><span className="font-display font-black text-[#FFCC00]" data-testid="valet-resumen-esperado">${Number(resumen?.efectivo_esperado || 0).toFixed(2)}</span></div>
                {Number(resumen?.total_gastos || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-[#F87171]">(−) Gastos y nóminas</span><span className="font-mono text-[#F87171]">−${Number(resumen?.total_gastos || 0).toFixed(2)}</span></div>
                )}
                {Number(resumen?.retiro_propinas || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2"><span className="text-[#F87171]">(−) Retiro de propinas (tarjeta)</span><span className="font-mono text-[#F87171]">−${Number(resumen?.retiro_propinas || 0).toFixed(2)}</span></div>
                )}
                {Number(resumen?.monto_factor_riesgo_aplicado || 0) > 0 && (
                  <div className="flex justify-between px-4 py-2" data-testid="valet-resumen-factor-riesgo"><span className="text-[#34D399]">(+) Factor de Riesgo Valet (Aporte de operador)</span><span className="font-mono text-[#34D399]">+${Number(resumen?.monto_factor_riesgo_aplicado || 0).toFixed(2)}</span></div>
                )}
                <div className="flex justify-between px-4 py-3 bg-[#FFCC00]/10 border-t-2 border-[#FFCC00]"><span className="text-xs uppercase tracking-wider font-black text-[#FFCC00]">Total efectivo a entregar (Caja + Factor de Riesgo)</span><span className="font-display font-black text-[#FFCC00] text-lg" data-testid="valet-resumen-neto">${Number(resumen?.efectivo_neto_entregar || 0).toFixed(2)}</span></div>
              </div>
              <div className="border border-[#6366F1]/40 bg-[#6366F1]/10 p-3" data-testid="valet-cierre-inventario-patio">
                <div className="flex items-center gap-2 text-[#A5B4FC] text-xs font-bold uppercase tracking-wider mb-1">
                  <Moon className="h-4 w-4" /> Vehículos en patio que pernoctan: <span>{resumen?.vehiculos_en_patio ?? 0}</span>
                </div>
                {(resumen?.folios_patio || []).length > 0 ? (
                  <p className="text-[11px] text-white/60 leading-relaxed">{(resumen?.folios_patio || []).map((f) => f.placa ? `${f.folio} · ${f.placa}` : f.folio).join(" ,  ")}</p>
                ) : (
                  <p className="text-[11px] text-white/40">Patio despejado. No hay vehículos heredados para el siguiente turno.</p>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Efectivo contado (debe igualar el neto a entregar · admite negativo si hubo gastos altos)</label>
                <CurrencyField inputMode="decimal" allowNegative value={efectivo} onChange={(v) => setEfectivo(v)} placeholder="0.00"
                  data-testid="valet-efectivo-cierre-input" className="mt-1 h-12 rounded-none bg-white/5 border-white/20 text-white text-lg font-display" />
              </div>
              {efectivo !== "" && efectivo != null && resumen && (() => {
                const diff = parseFloat(efectivo) - Number(resumen.efectivo_neto_entregar);
                const cuadra = Math.abs(diff) < 0.005;
                return (
                  <div data-testid="valet-cierre-diferencia" className={`flex justify-between items-center px-4 py-2.5 border ${cuadra ? "border-[#10B981]/40 bg-[#10B981]/10" : "border-[#EF4444]/40 bg-[#EF4444]/10"}`}>
                    <span className="text-xs uppercase tracking-wider font-bold text-white/70">{cuadra ? "Caja cuadrada" : diff > 0 ? "Sobrante" : "Faltante"}</span>
                    <span className={`font-display font-black ${cuadra ? "text-[#34D399]" : "text-[#F87171]"}`}>{diff >= 0 ? "+" : "−"}${Math.abs(diff).toFixed(2)}</span>
                  </div>
                );
              })()}
              <button onClick={cerrarTurno} disabled={cerrando} data-testid="valet-confirmar-cierre-btn"
                className="btn-hard w-full h-12 bg-[#EF4444] text-white border border-[#EF4444] font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                {cerrando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />} {cerrando ? "Cerrando…" : "Confirmar cierre"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- Cerrar EVENTO · Valet sin costo (auditoría de Factor de Riesgo, sin cuadre de caja) --- */}
      <Dialog open={noCostOpen} onOpenChange={setNoCostOpen}>
        <DialogContent className="rounded-none border-white/20 bg-[#0A0A0A] text-white sm:max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="valet-evento-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-white">Cerrar evento · Valet sin costo</DialogTitle>
            <DialogDescription className="text-white/50">Este evento no genera corte financiero. Se registrará la auditoría de folios y el Factor de Riesgo a cobrar al encargado.</DialogDescription>
          </DialogHeader>
          {noCostLoading ? (
            <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FFCC00]" /></div>
          ) : (
            <div className="space-y-4 pt-1" data-testid="valet-evento-resumen">
              <div className="border border-white/10 divide-y divide-white/10 text-sm">
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Encargado</span><span className="font-mono">{noCostResumen?.operador || "—"}</span></div>
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Tickets usados</span><span className="font-mono" data-testid="evento-folios-totales">{noCostResumen?.folios_totales_turno ?? 0}</span></div>
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Cancelados / Cortesías</span><span className="font-mono">{noCostResumen?.folios_cancelados_cortesia ?? 0}</span></div>
                <div className="flex justify-between px-4 py-2"><span className="text-white/60">Folios netos (sujetos a factor)</span><span className="font-mono">{noCostResumen?.folios_factor_riesgo ?? 0}</span></div>
                <div className="flex justify-between px-4 py-3 bg-[#10B981]/10 border-t-2 border-[#10B981]"><span className="text-xs uppercase tracking-wider font-black text-[#34D399]">Factor de Riesgo a cobrar</span><span className="font-display font-black text-[#34D399] text-lg" data-testid="evento-total-factor">${Number(noCostResumen?.monto_factor_riesgo_aplicado || 0).toFixed(2)}</span></div>
              </div>
              <div className="border border-[#6366F1]/40 bg-[#6366F1]/10 p-3">
                <div className="flex items-center gap-2 text-[#A5B4FC] text-xs font-bold uppercase tracking-wider mb-1">
                  <Moon className="h-4 w-4" /> Vehículos aún en patio: <span>{noCostResumen?.vehiculos_en_patio ?? 0}</span>
                </div>
              </div>
              <button onClick={cerrarEventoSinCosto} disabled={noCostSaving} data-testid="valet-confirmar-evento-btn"
                className="btn-hard w-full h-12 bg-[#EF4444] text-white border border-[#EF4444] font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
                {noCostSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />} Confirmar cierre de evento
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- Comprobante térmico de corte (oculto en pantalla; visible solo al imprimir) --- */}
      {corteReceipt ? (
        <div data-testid="valet-corte-thermal">
          <CorteThermalReceipt corte={corteReceipt} paperWidth={paperWidth} />
        </div>
      ) : null}

      {/* --- Comprobante térmico de GASTO (oculto en pantalla; visible solo al imprimir) --- */}
      {gastoReceipt ? (
        <div data-testid="valet-gasto-thermal-wrap">
          <GastoThermalReceipt gasto={gastoReceipt} paperWidth={paperWidth} />
        </div>
      ) : null}
    </>
  );
});

export default CajaTurnoBar;
